import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  LinearProgress,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { getStatusIcon, getStatusColor, getStatusLabel } from '../../utils/statusFormatters';
import { esES } from '@mui/x-data-grid/locales';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { paymentService } from '../../services/api';
import { exportToExcel } from '../../utils/excelExport';
import dayjs from 'dayjs';

const months = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

interface PaymentRow {
  id: number;
  student_id: number;
  parent_id: number;
  month: string;           // "Enero", "Febrero", etc.
  year: number;
  amount: string;          // "2500.00"
  total_due?: number;
  paid_amount?: number;
  pending_balance?: number;
  is_completed?: boolean;
  payment_history?: any[];
  last_partial_payment_date?: string;
  payment_date: string;
  status: string;
  payment_proof_url: string | null;
  comments: string | null;
  bank_reference: string | null;
  created_at: string;
  updated_at: string;
  student: {
    id: number;
    first_name: string;
    last_name: string;
    grade: number;
    // ... resto de campos
  };
  parent: {
    id: number;
    russian_name: string;
    spanish_name: string;
    // ... 
  };
}

const PaymentList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  
  // Filtros
  const [filters, setFilters] = useState({
    month: '',
    year: dayjs().year(),
    status: '',
    search: ''
  });
  
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [proofDialogOpen, setProofDialogOpen] = useState(false);

  // Resumen del mes
  const [monthlySummary, setMonthlySummary] = useState<any>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    loadPayments();
    loadMonthlySummary();
  }, [paginationModel, filters]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      
      const params: any = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize,
        ...filters
      };
      
      // Limpiar filtros vacíos
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await paymentService.getAllMonthly(params);
      
      setPayments(response.data.data);
      setTotal(response.data.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar pagos');
    } finally {
      setLoading(false);
    }
  };

  const loadMonthlySummary = async () => {
    try {
      setSummaryLoading(true);
      const response = await paymentService.getMonthlySummary(
        filters.month || dayjs().format('MMMM').toLowerCase(),
        filters.year || dayjs().year()
      );
      setMonthlySummary(response.data);
    } catch (err) {
      console.error('Error loading summary:', err);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleExport = () => {
    const data = payments.map(payment => ({
      'ID': payment.id,
      'Estudiante': `${payment.student?.first_name} ${payment.student?.last_name}`,
      'Grado': payment.student?.grade,
      'Mes': payment.month,
      'Año': payment.year,
      'Monto': payment.amount,
      'Fecha Pago': payment.payment_date ? dayjs(payment.payment_date).format('DD/MM/YYYY') : '',
      'Estado': payment.status === 'paid' ? 'Pagado' :
                payment.status === 'pending' ? 'Pendiente' : payment.status,
      'Referencia': payment.bank_reference || '',
      'Padre': payment.parent?.spanish_name || payment.parent?.russian_name || ''
    }));
    
    exportToExcel(data, 'pagos_mensuales');
  };

  const handleDelete = async () => {
    if (!selectedPayment) return;
    
    try {
      await paymentService.deleteMonthly(selectedPayment.id);
      loadPayments();
      setDeleteDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar pago');
    }
  };

  const columns: GridColDef<PaymentRow>[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'student',
      headerName: 'Estudiante',
      flex: 1.5,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.student?.first_name} {params.row.student?.last_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Grado: {params.row.student?.grade}
          </Typography>
        </Box>
      )
    },
    {
      field: 'month_year',
      headerName: 'Mes/Año',
      //width: 120,
      flex: 0.7,
      renderCell: (params) => (
        <Typography fontWeight="medium">
          {params.row.month}/{params.row.year}
        </Typography>
      )
    },
    {
      field: 'amount',
      headerName: 'Monto',
      //width: 100,
      flex: 0.5,
      renderCell: (params) => (
        <Typography fontWeight="medium">
          ₽{params.row.amount.toLocaleString()}
        </Typography>
      )
    },
    {
      field: 'progress',
      headerName: 'Progreso',
      width: 150,
      renderCell: (params) => {
        const total = Number(params.row.total_due || params.row.amount);
        const paid = Number(params.row.paid_amount || 0);
        const percentage = total > 0 ? (paid / total) * 100 : 0;
        
        if (params.row.is_completed) {
          return <Chip label="Completado" color="success" size="small" />;
        }
        
        if (paid > 0) {
          return (
            <Box sx={{ width: '100%' }}>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="caption">
                  {percentage.toFixed(0)}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={percentage}
                color="warning"
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          );
        }
        
        return <Chip label="Pendiente" color="error" size="small" />;
      }
    },
    {
      field: 'payment_date',
      headerName: 'Fecha Pago',
      //width: 120,
      flex: 0.8,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.payment_date 
            ? dayjs(params.row.payment_date).format('DD [de] MMMM [de] YYYY')
            : '-'
          }
        </Typography>
      )
    },
    {
      field: 'parent',
      headerName: 'Pagado por',
      //width: 180,
      flex: 0.8,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {params.row.parent?.spanish_name || params.row.parent?.russian_name || 'N/A'}
          </Typography>
          {params.row.bank_reference && (
            <Typography variant="caption" color="text.secondary">
              Ref: {params.row.bank_reference}
            </Typography>
          )}
        </Box>
      )
    },
    {
      field: 'status',
      headerName: 'Estado',
      //width: 120,
      flex: 0.6,
      renderCell: (params) => (
        <Chip
          icon={getStatusIcon(params.value)}
          label={params.value === 'paid' ? 'Pagado' :
                 params.value === 'pending' ? 'Pendiente' :
                 params.value === 'partial' ? 'Por cuotas' :
                 params.value === 'baja' ? 'Baja' :
                 params.value === 'alta' ? 'Alta' : params.value}
          color={getStatusColor(params.value) as any}
          size="small"
        />
      )
    },
    {
      field: 'proof',
      headerName: 'Comprobante',
      //width: 100,
      flex: 0.8,
      renderCell: (params) => (
        params.row.payment_proof_url ? (
          <Tooltip title="Ver comprobante">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPayment(params.row);
                setProofDialogOpen(true);
              }}
            >
              <ReceiptIcon color="primary" />
            </IconButton>
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Sin comprobante
          </Typography>
        )
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      //width: 120,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver detalles">
            <IconButton
              size="small"
              onClick={() => navigate(`/monthly-payments/${params.row.id}`)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={() => navigate(`/monthly-payments/${params.row.id}/edit`)}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPayment(params.row);
                setDeleteDialogOpen(true);
              }}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Pagos Mensuales
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ mr: 1 }}
          >
            Exportar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/monthly-payments/new')}
            disabled={!['admin', 'manager'].includes(user?.role || '')}
          >
            Nuevo Pago
          </Button>
        </Box>
      </Box>

      {/* Resumen del Mes */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Total Pagos
              </Typography>
              <Typography variant="h4">
                {monthlySummary?.summary?.total_payments || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {filters.month || 'Este mes'} {filters.year}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
            <CardContent>
              <Typography variant="body2">
                Pagados
              </Typography>
              <Typography variant="h4">
                ₽{(monthlySummary?.summary?.paid_amount || 0).toLocaleString()}
              </Typography>
              <Typography variant="caption">
                {monthlySummary?.summary?.paid_count || 0} estudiantes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <CardContent>
              <Typography variant="body2">
                Pendientes
              </Typography>
              <Typography variant="h4">
                {monthlySummary?.summary?.pending_count || 0}
              </Typography>
              <Typography variant="caption">
                estudiantes sin pagar
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
            <CardContent>
              <Typography variant="body2">
                Bajas
              </Typography>
              <Typography variant="h4">
                {monthlySummary?.summary?.baja_count || 0}
              </Typography>
              <Typography variant="caption">
                este mes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              placeholder="Buscar estudiante..."
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Mes</InputLabel>
              <Select
                value={filters.month}
                label="Mes"
                onChange={(e) => setFilters({...filters, month: e.target.value})}
              >
                <MenuItem value="">Todos</MenuItem>
                {months.map(month => (
                  <MenuItem key={month} value={month}>
                    {month.charAt(0).toUpperCase() + month.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              fullWidth
              type="number"
              label="Año"
              value={filters.year}
              onChange={(e) => setFilters({...filters, year: parseInt(e.target.value)})}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={filters.status}
                label="Estado"
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="paid">Pagado</MenuItem>
                <MenuItem value="pending">Pendiente</MenuItem>
                <MenuItem value="baja">Baja</MenuItem>
                <MenuItem value="finalizado">Finalizado</MenuItem>
                <MenuItem value="alta">Alta</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={() => loadPayments()}
                fullWidth
              >
                Filtrar
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setFilters({
                    month: '',
                    year: dayjs().year(),
                    status: '',
                    search: ''
                  });
                }}
              >
                <RefreshIcon />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabla de Pagos */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={payments}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={total}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowClick={(params) => navigate(`/monthly-payments/${params.id}`)}
          disableRowSelectionOnClick
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        />
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de eliminar este pago?
          </Typography>
          {selectedPayment && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Estudiante:</strong> {selectedPayment.student?.first_name} {selectedPayment.student?.last_name}
              </Typography>
              <Typography variant="body2">
                <strong>Período:</strong> {selectedPayment.month} {selectedPayment.year}
              </Typography>
              <Typography variant="body2">
                <strong>Monto:</strong> ₽{selectedPayment.amount}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Proof Dialog */}
      <Dialog
        open={proofDialogOpen}
        onClose={() => setProofDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Comprobante de Pago
        </DialogTitle>
        <DialogContent>
          {selectedPayment?.payment_proof_url && (
            <Box sx={{ textAlign: 'center' }}>
              {selectedPayment.payment_proof_url.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                <Box
                  component="img"
                  src={selectedPayment.payment_proof_url}
                  alt="Comprobante"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <ReceiptIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography gutterBottom>
                    Archivo PDF de comprobante
                  </Typography>
                  <Button
                    variant="contained"
                    href={selectedPayment.payment_proof_url}
                    target="_blank"
                  >
                    Abrir PDF
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProofDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentList;